// The value chart (U-32), after DefiLlama's TVL chart: the big current value
// and its change, a smooth line over a purple gradient, a crosshair with a
// tooltip (date, value, estimated, the day's net flow and coverage events),
// a thin net-flow bar strip, small dots on the axis where products joined
// tracking, and a range navigator: preset buttons plus a brush with
// draggable handles. CSV and PNG export. Hand-rolled SVG, no chart library;
// the math lives in value-chart.ts.

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";

import { coverageSummary, type CoverageEvent } from "../class-history";
import { downloadChartPng } from "../chart-export";
import { csvFileName, downloadCsv, toCsv } from "../csv";
import { flowText } from "../flows";
import { nearestIndex } from "../stablecoins-chart";
import {
  areaPath,
  changeText,
  clampWindow,
  likeForLikeChange,
  fullWindow,
  monotonePath,
  presetWindow,
  sameWindow,
  spanLabel,
  timeTicks,
  tooltipDate,
  valueDomain,
  windowChange,
  windowPoints,
  type ValuePoint,
  type Window,
} from "../value-chart";
import styles from "./ValueChart.module.css";

export interface ChartPreset {
  id: string;
  label: string;
  /** Days back from the newest point; null for the whole series. */
  days: number | null;
}

export const DEFAULT_PRESETS: readonly ChartPreset[] = [
  { id: "7d", label: "7D", days: 7 },
  { id: "30d", label: "30D", days: 30 },
  { id: "90d", label: "90D", days: 90 },
  { id: "1y", label: "1Y", days: 365 },
  { id: "all", label: "All", days: null },
];

/** What the tooltip can say about a point beyond its value. */
export interface PointDetail {
  estimated?: boolean;
  /** DQ-16: the point holds an upstream value the jump guard has not confirmed. */
  unconfirmed?: boolean;
  /** How the point was estimated ("reconstructed from chain state"); shown instead of "estimated". */
  estimate?: string | null;
  /** A weekly point: the date is the week's last stored day and its net flow is the week's. */
  weekly?: boolean;
  /** Value added minus removed by products joining or leaving tracking that day, USD; null when not computed. */
  coverageUsd?: number | null;
  entries?: number | null;
  events?: readonly CoverageEvent[];
}

export interface ValueChartProps {
  /** Chart name, for the header, aria label and exports. */
  title: string;
  points: readonly ValuePoint[];
  /** Compact value text for the header and axis ("$3.7B", "3.90%"). */
  format: (value: number) => string;
  /** Exact value text for the tooltip; defaults to `format`. */
  exact?: (value: number) => string;
  /** Change as a percent of value, or in points (rates). */
  changeKind?: "pct" | "pt";
  /**
   * The header shows the like-for-like change (U-32b): net of products joining or leaving tracking, from
   * `details[].coverageUsd`, with the coverage as a second note; none at all when a day's coverage is unknown.
   * Off, the raw change (a single token, a rate, DefiLlama's stablecoin total).
   */
  likeForLike?: boolean;
  /** Per-point extras, by time. */
  details?: ReadonlyMap<number, PointDetail>;
  /** Daily net flows, [time, USD | null], drawn as a bar strip under the line. */
  flows?: ReadonlyArray<readonly [number, number | null]>;
  /** Preset windows; null hides them (the token panel picks its range by fetching). */
  presets?: readonly ChartPreset[] | null;
  defaultPreset?: string;
  /** Hours matter (sub-daily series): the tooltip shows the time. */
  withTime?: boolean;
  /** File name stem for exports. */
  exportName: string;
}

/** The container's width, following resizes; 0 until measured. */
function useWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (w: number) => w > 0 && setWidth(Math.round(w));
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver((entries) => measure(entries[0]?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const PAD = { left: 52, right: 14, top: 12 };
const FLOW_STRIP = 34;
const AXIS = 22;
const BRUSH = 40;
const HANDLE = 8;

export function ValueChart({
  title,
  points,
  format,
  exact = format,
  changeKind = "pct",
  likeForLike = false,
  details,
  flows,
  presets = DEFAULT_PRESETS,
  defaultPreset = "all",
  withTime = false,
  exportName,
}: ValueChartProps) {
  const [frameRef, width] = useWidth();
  const svgRef = useRef<SVGSVGElement>(null);
  const gradientId = useId().replace(/:/g, "");
  const full = useMemo(() => fullWindow(points), [points]);
  const presetDays = (id: string) => presets?.find((p) => p.id === id)?.days ?? null;
  const [win, setWin] = useState<Window>(() => presetWindow(points, presetDays(defaultPreset)));
  const [hover, setHover] = useState<number | null>(null);

  // New data (another class, range or series) starts again from the default preset.
  const dataKey = `${points.length}:${full.from}:${full.to}`;
  useEffect(() => {
    setWin(presetWindow(points, presetDays(defaultPreset)));
    setHover(null);
  }, [dataKey]);

  const shown = useMemo(() => windowPoints(points, win), [points, win]);
  const narrow = width < 480;
  const padLeft = narrow ? 44 : PAD.left;
  const plotH = narrow ? 170 : 220;
  const windowFlows = useMemo(() => (flows ?? []).filter(([t, v]) => t >= win.from && t <= win.to && v !== null) as Array<[number, number]>, [flows, win]);
  const hasFlows = windowFlows.length > 0;
  const height = PAD.top + plotH + (hasFlows ? FLOW_STRIP : 0) + AXIS;
  const innerW = Math.max(1, width - padLeft - PAD.right);
  // A little inner margin so the end points' dots are not cut by the frame.
  const inset = 6;
  const spanT = Math.max(1, win.to - win.from);
  const x = (t: number) => padLeft + inset + ((t - win.from) / spanT) * (innerW - inset * 2);
  const domain = useMemo(() => valueDomain(shown.map(([, v]) => v), narrow ? 3 : 4), [shown, narrow]);
  const y = (v: number) => PAD.top + plotH - ((v - domain.lo) / (domain.hi - domain.lo || 1)) * plotH;
  const pixels = shown.map(([t, v]) => ({ x: x(t), y: y(v) }));
  const line = monotonePath(pixels);
  const baseline = PAD.top + plotH;
  const area = areaPath(line, pixels, baseline);
  const ticks = width > 0 ? timeTicks(win.from, win.to, narrow ? 3 : 6, withTime ? 0 : 86_400_000) : [];

  const last = shown[shown.length - 1];
  const hovered = hover !== null ? shown[hover] : undefined;
  const lfl = likeForLike ? likeForLikeChange(shown, (t) => details?.get(t)?.coverageUsd) : null;
  const change = lfl ? lfl.pct : windowChange(shown, changeKind);
  const coverageNote = lfl && lfl.pct !== null && lfl.coverageUsd !== null && Math.abs(lfl.coverageUsd) >= 0.01 ? `${lfl.coverageUsd > 0 ? "+" : "−"}${format(Math.abs(lfl.coverageUsd))} coverage ${lfl.coverageUsd > 0 ? "growth" : "loss"}` : null;
  // A short series fits every preset; the widest one that matches is the honest label ("All", not "7D").
  const activePreset = [...(presets ?? [])].reverse().find((p) => sameWindow(presetWindow(points, p.days), win))?.id ?? null;

  // ---- hover

  function pointerAt(event: ReactPointerEvent<SVGSVGElement>): void {
    if (!shown.length) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const t = win.from + ((px - padLeft - inset) / (innerW - inset * 2)) * spanT;
    setHover(nearestIndex(shown, t));
  }

  function onKey(event: KeyboardEvent<SVGSVGElement>): void {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const at = hover ?? shown.length - 1;
    setHover(Math.max(0, Math.min(shown.length - 1, at + (event.key === "ArrowRight" ? 1 : -1))));
  }

  // ---- brush

  const brushW = Math.max(1, width - padLeft - PAD.right);
  const fullSpan = Math.max(1, full.to - full.from);
  const bx = (t: number) => padLeft + ((t - full.from) / fullSpan) * brushW;
  const bt = (px: number) => full.from + ((px - padLeft) / brushW) * fullSpan;
  const overview = useMemo(() => {
    if (points.length < 2) return "";
    const vals = points.map(([, v]) => v);
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const py = (v: number) => 6 + (BRUSH - 12) * (1 - (v - lo) / (hi - lo || 1));
    return monotonePath(points.map(([t, v]) => ({ x: bx(t), y: py(v) })));
  }, [points, width, padLeft]);
  const drag = useRef<{ mode: "from" | "to" | "move"; startX: number; start: Window } | null>(null);

  function brushDown(event: ReactPointerEvent<SVGSVGElement>): void {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const left = bx(win.from);
    const right = bx(win.to);
    const mode = Math.abs(px - left) <= HANDLE ? "from" : Math.abs(px - right) <= HANDLE ? "to" : px > left && px < right ? "move" : null;
    if (mode === null) {
      // A click outside the selection centres the window there.
      const span = win.to - win.from;
      setWin(clampWindow({ from: bt(px) - span / 2, to: bt(px) + span / 2 }, points));
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { mode, startX: px, start: win };
  }

  function brushMove(event: ReactPointerEvent<SVGSVGElement>): void {
    const d = drag.current;
    if (!d) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const dt = ((event.clientX - rect.left - d.startX) / brushW) * fullSpan;
    const next =
      d.mode === "move"
        ? { from: d.start.from + dt, to: d.start.to + dt }
        : d.mode === "from"
          ? { from: Math.min(d.start.from + dt, d.start.to), to: d.start.to }
          : { from: d.start.from, to: Math.max(d.start.to + dt, d.start.from) };
    setWin(clampWindow(next, points));
    setHover(null);
  }

  function brushKey(event: KeyboardEvent<SVGSVGElement>): void {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const step = (win.to - win.from) / 10;
    const dt = event.key === "ArrowRight" ? step : -step;
    setWin(clampWindow({ from: win.from + dt, to: win.to + dt }, points));
  }

  // ---- exports

  function exportCsv(): void {
    const flowAt = new Map((flows ?? []).map(([t, v]) => [t, v]));
    const rows = shown.map(([t, v]) => ({ t, v, d: details?.get(t), f: flowAt.get(t) }));
    const csv = toCsv(rows, [
      { header: withTime ? "Time (UTC)" : "Date (UTC)", value: (r) => (withTime ? new Date(r.t).toISOString() : new Date(r.t).toISOString().slice(0, 10)) },
      { header: title, value: (r) => r.v },
      ...(details ? [{ header: "Estimated", value: (r: (typeof rows)[number]) => r.d?.estimate ?? (r.d?.estimated ? "yes" : "") }] : []),
      ...(details && [...details.values()].some((d) => d.weekly) ? [{ header: "Interval", value: (r: (typeof rows)[number]) => (r.d?.weekly ? "week" : "day") }] : []),
      ...(flows ? [{ header: "Net flow (USD)", value: (r: (typeof rows)[number]) => r.f ?? null }] : []),
    ]);
    downloadCsv(csvFileName(exportName, last ? new Date(last[0]).toISOString() : null), csv);
  }

  function exportPng(): void {
    if (!svgRef.current || !last) return;
    const subtitle = `${format(last[1])}${change !== null ? ` · ${changeText(change, changeKind)} ${spanLabel(win)}` : ""} · ${tooltipDate(win.from, false)} – ${tooltipDate(win.to, false)}`;
    void downloadChartPng(svgRef.current, `${csvFileName(exportName, new Date(last[0]).toISOString()).replace(/\.csv$/, "")}.png`, title, subtitle).catch(() => undefined);
  }

  // ---- render

  const flowMax = hasFlows ? Math.max(...windowFlows.map(([, v]) => Math.abs(v)), 1) : 1;
  const flowMid = baseline + FLOW_STRIP / 2;
  const daysShown = Math.max(1, spanT / 86_400_000);
  const barW = Math.max(2, Math.min(12, ((innerW - inset * 2) / (daysShown + 1)) * 0.6));
  const markers = details ? shown.filter(([t]) => (details.get(t)?.events?.length ?? 0) > 0) : [];
  const hoverX = hovered ? x(hovered[0]) : 0;
  const hoverDetail = hovered ? details?.get(hovered[0]) : undefined;
  const hoverFlow = hovered ? (flows ?? []).find(([t]) => t === hovered[0])?.[1] ?? null : null;
  const tipLeft = hovered ? Math.min(Math.max(hoverX + 12, 0), Math.max(0, width - 200)) : 0;
  const flipped = hovered ? hoverX + 212 > width : false;

  return (
    <div className={styles.chart}>
      <div className={styles.header}>
        <div>
          <div className={styles.value}>{hovered ? format(hovered[1]) : last ? format(last[1]) : "—"}</div>
          <div className={styles.change}>
            {hovered ? (
              <span className={styles.muted}>{tooltipDate(hovered[0], withTime)}</span>
            ) : change !== null ? (
              <>
                <span className={change > 0 ? "up" : change < 0 ? "down" : undefined}>{changeText(change, changeKind)}</span>{" "}
                <span className={styles.muted}>
                  {spanLabel(win)}
                  {likeForLike ? ", same products" : ""}
                  {coverageNote ? ` · ${coverageNote}` : ""}
                </span>
              </>
            ) : (
              <span className={styles.muted}>{points.length < 2 ? "one reading so far" : ""}</span>
            )}
          </div>
        </div>
        <div className={styles.tools}>
          {presets ? (
            <span aria-label="Chart range" className={styles.presets} role="group">
              {presets.map((p) => (
                <button aria-pressed={activePreset === p.id} className={styles.preset} data-active={activePreset === p.id} key={p.id} onClick={() => setWin(presetWindow(points, p.days))} type="button">
                  {p.label}
                </button>
              ))}
            </span>
          ) : null}
          <button className={styles.export} disabled={!shown.length} onClick={exportCsv} title="Download the points in view as CSV" type="button">
            .csv
          </button>
          <button className={styles.export} disabled={shown.length < 2} onClick={exportPng} title="Download the chart as an image" type="button">
            .png
          </button>
        </div>
      </div>

      <div className={styles.frame} ref={frameRef} style={{ minHeight: height }}>
        {points.length < 2 ? (
          <p className={styles.empty}>{points.length === 0 ? "No history published yet." : "One reading so far; the line needs two."}</p>
        ) : width > 0 ? (
          <>
            <svg
              aria-label={`${title}: ${shown.length} points from ${tooltipDate(win.from, withTime)} to ${tooltipDate(win.to, withTime)}, latest ${last ? exact(last[1]) : "unknown"}`}
              className={styles.svg}
              height={height}
              onKeyDown={onKey}
              onPointerLeave={() => setHover(null)}
              onPointerMove={pointerAt}
              ref={svgRef}
              role="img"
              tabIndex={0}
              viewBox={`0 0 ${width} ${height}`}
              width={width}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                  <stop className={styles.gradTop} offset="0%" />
                  <stop className={styles.gradBottom} offset="100%" />
                </linearGradient>
              </defs>
              {domain.ticks.map((v) => (
                <g key={v}>
                  <line className={styles.grid} x1={padLeft} x2={width - PAD.right} y1={y(v)} y2={y(v)} />
                  <text className={styles.axis} textAnchor="end" x={padLeft - 6} y={y(v) + 3}>
                    {format(v)}
                  </text>
                </g>
              ))}
              {area ? <path d={area} fill={`url(#${gradientId})`} /> : null}
              {line ? <path className={styles.line} d={line} /> : null}
              {markers.map(([t]) => (
                <circle className={styles.marker} cx={x(t)} cy={baseline} key={t} r={3} />
              ))}
              {hasFlows ? (
                <g>
                  <line className={styles.grid} x1={padLeft} x2={width - PAD.right} y1={flowMid} y2={flowMid} />
                  {windowFlows.map(([t, v]) => {
                    const h = (Math.abs(v) / flowMax) * (FLOW_STRIP / 2 - 3);
                    return <rect className={v >= 0 ? styles.flowIn : styles.flowOut} height={Math.max(v === 0 ? 0 : 1, h)} key={t} width={barW} x={x(t) - barW / 2} y={v >= 0 ? flowMid - h : flowMid} />;
                  })}
                </g>
              ) : null}
              {ticks.map((tick, i) => (
                <text className={styles.axis} key={tick.t} textAnchor={i === 0 && x(tick.t) < padLeft + 30 ? "start" : "middle"} x={x(tick.t)} y={height - 6}>
                  {tick.label}
                </text>
              ))}
              {hovered ? (
                <g aria-hidden>
                  <line className={styles.crosshair} x1={hoverX} x2={hoverX} y1={PAD.top} y2={baseline + (hasFlows ? FLOW_STRIP : 0)} />
                  <circle className={styles.dot} cx={hoverX} cy={y(hovered[1])} r={4} />
                </g>
              ) : null}
            </svg>
            {hovered ? (
              <div className={styles.tooltip} style={flipped ? { right: Math.max(0, width - hoverX + 12) } : { left: tipLeft }}>
                <div className={styles.tipDate}>
                  {hoverDetail?.weekly ? "week to " : ""}
                  {tooltipDate(hovered[0], withTime)}
                </div>
                <div className={styles.tipValue}>
                  {exact(hovered[1])}
                  {hoverDetail?.estimate ? (
                    <span className={styles.tipEstimated}>{hoverDetail.estimate}</span>
                  ) : hoverDetail?.estimated ? (
                    <span className={styles.tipEstimated}>estimated</span>
                  ) : null}
                  {hoverDetail?.unconfirmed ? (
                    <span className={styles.tipEstimated} title="Includes an upstream value held until on-chain data confirms it">
                      unconfirmed
                    </span>
                  ) : null}
                </div>
                {hoverFlow !== null ? (
                  <div className={hoverFlow > 0 ? "up" : hoverFlow < 0 ? "down" : undefined}>
                    Net flow{hoverDetail?.weekly ? " · week" : ""} {flowText(hoverFlow)}
                  </div>
                ) : null}
                {hoverDetail?.entries != null ? <div className={styles.muted}>{hoverDetail.entries} entries</div> : null}
                {hoverDetail?.events?.flatMap((e) => coverageSummary(e)).map((line) => (
                  <div className={styles.tipEvent} key={line}>
                    {line}
                  </div>
                ))}
              </div>
            ) : null}
            <svg
              aria-label="Range navigator: drag the handles or the window, or use the arrow keys"
              className={styles.brush}
              height={BRUSH}
              onKeyDown={brushKey}
              onPointerDown={brushDown}
              onPointerMove={brushMove}
              onPointerUp={() => (drag.current = null)}
              role="slider"
              aria-valuemin={full.from}
              aria-valuemax={full.to}
              aria-valuenow={win.to}
              aria-valuetext={`${tooltipDate(win.from, withTime)} to ${tooltipDate(win.to, withTime)}`}
              tabIndex={0}
              viewBox={`0 0 ${width} ${BRUSH}`}
              width={width}
            >
              <rect className={styles.brushBg} height={BRUSH - 4} width={brushW} x={padLeft} y={2} />
              {overview ? <path className={styles.brushLine} d={overview} /> : null}
              <rect className={styles.brushSel} height={BRUSH - 4} width={Math.max(2, bx(win.to) - bx(win.from))} x={bx(win.from)} y={2} />
              <rect className={styles.handle} height={BRUSH - 14} rx={2} width={6} x={bx(win.from) - 3} y={7} />
              <rect className={styles.handle} height={BRUSH - 14} rx={2} width={6} x={bx(win.to) - 3} y={7} />
            </svg>
          </>
        ) : null}
      </div>
    </div>
  );
}
